import React from 'react'
import NextError, { ErrorProps } from 'next/error'
import { NextPageContext } from 'next'

import { PageProps } from '@src/types'
import { getLanguageFromLocale, getMessagesFromLanguage } from '../i18n/utils'
import { getDomainFromLanguage } from '@common/utils/request-utils'

interface InitialProps {
    hasGetInitialPropsRun?: boolean
    err?: any
    title?: string
}
type ErrorComponentProps = InitialProps & ErrorProps & PageProps

const ErrorComponent = ({ statusCode, title, hasGetInitialPropsRun, err }: ErrorComponentProps) => {
    if (!hasGetInitialPropsRun && err) {
        // `getInitialProps` is not called in case of https://github.com/zeit/next.js/issues/8592.
        // As a workaround, we pass err via _app.js so it can be captured.
    }

    return <NextError statusCode={statusCode} title={title} />
}

ErrorComponent.getInitialProps = async (ctx: NextPageContext) => {
    const errorInitialProps: ErrorComponentProps = await NextError.getInitialProps(ctx)

    // Workaround for https://github.com/zeit/next.js/issues/8592, mark when getInitialProps has run.
    errorInitialProps.hasGetInitialPropsRun = true

    const { res, err, asPath, locale } = ctx

    const language = getLanguageFromLocale(locale)
    const domain = getDomainFromLanguage(language)
    const pageProps: PageProps = {
        requestUserConfig: { language, domain },
        messages: await getMessagesFromLanguage(language),
    }

    if (
        res && // Running on the server, the response object is available.
        //
        // Next.js will pass an err on the server if a page's `getInitialProps` threw or returned a Promise that
        // rejected.
        res.statusCode === 404
    ) {
        // Opinionated: do not track an exception for 404 errors.
        return { statusCode: 404, ...pageProps }
    }

    const errorComponentProps: ErrorComponentProps = { ...errorInitialProps, ...pageProps }

    // Running on the client (browser).
    //
    // Next.js will provide an err if:
    //  - a page's `getInitialProps` threw or returned a Promise that rejected.
    //  - an exception was thrown somewhere in the React lifecycle (render, componentDidMount, etc) that was caught by
    //    Next.js's React Error Boundary.
    if (err) {
        return errorComponentProps
    }
    return errorComponentProps
}

export default ErrorComponent
